import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormControl, FormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { UserService } from '../../../core/services/user.service';
import { CreateUserRequest, UserType } from '../../../core/models/user.model';
import { forkJoin, Subject, takeUntil } from 'rxjs';

import { NotificationService } from '../../../core/services/notification.service';
import { RoleService } from '../../../core/services/role.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ClientService } from '../../../core/services/client.service';
import { GroupService } from '../../../core/services/group.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RolePermissionService } from '../../../core/services/role-permission.service';
import { MatSelectModule } from '@angular/material/select';
import { HostListener } from '@angular/core';

interface Application {
  id: string;
  clientId: string;
  name: string;
  icon?: string;
  description?: string;
  roles?: Role[];
}

interface Role {
  id: string;
  name: string;
  description?: string;
  composite?: boolean;
  clientRole?: boolean;
  containerId?: string;
  title: string;
}

interface RoleSelection {
  [appId: string]: {
    [roleId: string]: boolean;
  };
}

interface IesItem {
  sigle: string;
  label: string;
}

@Component({
  selector: 'app-user-create',
  templateUrl: './user-create.component.html',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./user-create.component.scss']
})
export class UserCreateComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  userForm!: FormGroup;
  isLoading = false;
  showSnackbar = false;
  isErrorSnackbar = false;
  snackbarMessage = '';
  activeTab = 1;
  isSaving = false;

  applications: Application[] = [];
  selectedApplications: Application[] = [];
  filteredApplications: Application[] = [];
  appSearchTerm = '';
  expandedAppId: string | null = null;
  roleSelection: RoleSelection = {};
  userId = '';

  userTypes: { value: UserType; label: string; allowed: boolean }[] = [];
  selectedUserType: UserType = UserType.SCA_ETABLISSEMENT_ADMIN;

  // Données IES / Établissements
  iesList: IesItem[] = [];
  etablissementsList: IesItem[] = [];

  // Contrôles de recherche pour les autocomplete
  iesSearchControl = new FormControl('');
  etabSearchControl = new FormControl('');

  filteredIesList: IesItem[] = [];
  filteredEtabList: IesItem[] = [];

  // Chargement
  isLoadingIes = false;
  isLoadingEtab = false;

  currentUserIesCode: string | null = null;
  currentUserIesSigle: string | null = null;
  isCurrentUserIesAdmin = false;

  readonly USER_TYPE = 'userType';
  readonly SIGLE_IES = 'sigleIes';
  readonly SIGLE_ETABLISSEMENT = 'sigleEtablissement';

  isEtabDropdownOpen = false;

  // Règles de visibilité des applications/rôles selon le type d'utilisateur cible
  //
  // - excludedRolePatterns : regex (case-insensitive) testées sur `name` ET `title`
  // - excludedApplications : fragments testés (en includes) sur id / name / clientId
  //
  // SUPER_ADMIN       → aucune restriction
  // IES_ADMIN         → masquer les rôles « Super Admin »
  // ETABLISSEMENT_ADMIN → masquer rôles « Super Admin » + « IES Admin »
  //                       + masquer l'application « Habilitation »
  // ─────────────────────────────────────────────────────────────────────────────
  private static readonly VISIBILITY_RULES: Record<UserType, {
    excludedRolePatterns: RegExp[];
    excludedApplications: string[];
  }> = {
    [UserType.SCA_SUPER_ADMIN]: {
      excludedRolePatterns: [],
      excludedApplications: []
    },
    [UserType.SCA_IES_ADMIN]: {
      excludedRolePatterns: [/super[\s_-]?admin/i],
      excludedApplications: []
    },
    [UserType.SCA_ETABLISSEMENT_ADMIN]: {
      excludedRolePatterns: [/super[\s_-]?admin/i, /ies[\s_-]?admin/i],
      excludedApplications: ['habilitation']
    },
    [UserType.SCA_ADMIN_AUTRE_ENTITE]: {
      excludedRolePatterns: [],
      excludedApplications: []
    }
  };

  /** Vue brute des applications telles que renvoyées par le backend (immuable). */
  private allApplications: Application[] = [];

  /** État d'ouverture du dropdown IES */
  isIesDropdownOpen = false;

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private groupService: GroupService,
    private roleService: RoleService,
    private notificationService: NotificationService,
    private snackBar: MatSnackBar,
    private clientService: ClientService,
    private rolePermissionService: RolePermissionService
  ) { }

  ngOnInit(): void {
    this.createForm();
    this.loadAllowedUserTypes();
    this.setupUserTypeListener();
    this.loadApplications();
    this.setupSearchFilters();

    this.currentUserIesCode = this.rolePermissionService.getCurrentUserIesCode();
    this.isCurrentUserIesAdmin = this.rolePermissionService.hasRole('SCA_IES_ADMIN');

    // Ajuster le champ IES si l'admin est IES_ADMIN
    if (this.isCurrentUserIesAdmin && this.currentUserIesCode) {
      this.userService.getSigleByCode(this.currentUserIesCode).subscribe({
        next: (sigle) => {
          this.currentUserIesSigle = sigle;
          this.adjustIesFieldForIesAdmin();
        },
        error: (err) => {
          console.error('Erreur récupération sigle IES', err);
          this.currentUserIesSigle = this.currentUserIesCode; // fallback
          this.adjustIesFieldForIesAdmin();
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  static atLeastOneSelected(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (Array.isArray(value) && value.length > 0) return null;
    return { required: true };
  }

  // Ajustement du champ IES (désactivé + pré-rempli)
  private adjustIesFieldForIesAdmin(): void {
    const sigleIesCtrl = this.userForm.get(this.SIGLE_IES);
    if (!sigleIesCtrl || !this.currentUserIesSigle) return;

    // Désactiver le contrôle et lui donner la valeur de l'IES courant
    sigleIesCtrl.disable();
    sigleIesCtrl.setValue(this.currentUserIesSigle);

    // Si le type d'utilisateur à créer est ETABLISSEMENT_ADMIN, charger ses établissements
    const currentType = this.userForm.get(this.USER_TYPE)?.value;
    if (currentType === UserType.SCA_ETABLISSEMENT_ADMIN) {
      this.onIesSelectionChange(this.currentUserIesSigle);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Initialisation du formulaire avec validation email améliorée
  // ─────────────────────────────────────────────────────────────────────────────
  private createForm(): void {
    this.userForm = this.fb.group({
      username: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email, Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)]],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      [this.USER_TYPE]: [UserType.SCA_ETABLISSEMENT_ADMIN, Validators.required],
      [this.SIGLE_IES]: [{ value: null, disabled: true }],
      [this.SIGLE_ETABLISSEMENT]: [[], [Validators.required, UserCreateComponent.atLeastOneSelected]]
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Filtrage des listes pour autocomplete
  // ─────────────────────────────────────────────────────────────────────────────
  private setupSearchFilters(): void {
    this.iesSearchControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(searchTerm => {
        this.filteredIesList = this.filterIesList(searchTerm || '');
      });

    this.etabSearchControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(searchTerm => {
        this.filteredEtabList = this.filterEtabList(searchTerm || '');
      });
  }

  private filterIesList(searchTerm: string): IesItem[] {
    if (!searchTerm) return this.iesList;
    const term = searchTerm.toLowerCase();
    return this.iesList.filter(ies =>
      ies.label.toLowerCase().includes(term) ||
      ies.sigle.toLowerCase().includes(term)
    );
  }

  private filterEtabList(searchTerm: string): IesItem[] {
    if (!searchTerm) return this.etablissementsList;
    const term = searchTerm.toLowerCase();
    return this.etablissementsList.filter(etab =>
      etab.label.toLowerCase().includes(term) ||
      etab.sigle.toLowerCase().includes(term)
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Sélection dans l'autocomplete
  // ─────────────────────────────────────────────────────────────────────────────
  onIesSelected(event: any): void {
    const sigle = event.option.value;
    this.userForm.get(this.SIGLE_IES)?.setValue(sigle);
    this.onIesSelectionChange(sigle);
  }

  onEtabSelected(event: any): void {
    const sigle = event.option.value;
    this.userForm.get(this.SIGLE_ETABLISSEMENT)?.setValue(sigle);
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Types d'utilisateurs autorisés
  // ─────────────────────────────────────────────────────────────────────────────
  private loadAllowedUserTypes(): void {
    const isSuper = this.rolePermissionService.hasRole('SCA_SUPER_ADMIN');
    const isIesAdmin = this.rolePermissionService.hasRole('SCA_IES_ADMIN');

    this.userTypes = [
      { value: UserType.SCA_SUPER_ADMIN, label: 'Super Administrateur', allowed: isSuper },
      { value: UserType.SCA_IES_ADMIN, label: 'Administrateur IES', allowed: isSuper },
      { value: UserType.SCA_ETABLISSEMENT_ADMIN, label: 'Administrateur Établissement', allowed: isSuper || isIesAdmin },
      { value: UserType.SCA_ADMIN_AUTRE_ENTITE, label: 'Admin autre entité', allowed: isSuper  }
    ];

    const firstAllowed = this.userTypes.find(t => t.allowed);
    if (firstAllowed) {
      this.userForm.get(this.USER_TYPE)?.setValue(firstAllowed.value);
      this.onUserTypeChange(firstAllowed.value);
    }
  }

  get allowedUserTypes() {
    return this.userTypes.filter(type => type.allowed);
  }

  private setupUserTypeListener(): void {
    this.userForm.get(this.USER_TYPE)?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(type => this.onUserTypeChange(type));
  }

  onUserTypeChange(type: UserType): void {
    this.selectedUserType = type;
    const sigleIesCtrl = this.userForm.get(this.SIGLE_IES);
    const sigleEtabCtrl = this.userForm.get(this.SIGLE_ETABLISSEMENT);

    // Réinitialiser les champs dépendants
    if (!this.isCurrentUserIesAdmin) {
      // Pour les super admins : comportement existant
      sigleIesCtrl?.reset(null);
    }

    sigleEtabCtrl?.setValue([]);
    this.etablissementsList = [];
    this.etabSearchControl.setValue('');
    this.filteredEtabList = [];

    if (type === UserType.SCA_SUPER_ADMIN) {
      if (this.isCurrentUserIesAdmin) {
        // Un IES_ADMIN ne devrait normalement pas pouvoir créer un SUPER_ADMIN,
        // mais par sécurité on désactive et on vide
        sigleIesCtrl?.disable();
        sigleEtabCtrl?.disable();
        sigleIesCtrl?.clearValidators();
        sigleEtabCtrl?.clearValidators();
      } else {
        sigleIesCtrl?.disable();
        sigleEtabCtrl?.disable();
      }
      sigleIesCtrl?.clearValidators();
      sigleEtabCtrl?.clearValidators();
    } else if (type === UserType.SCA_IES_ADMIN) {
      if (this.isCurrentUserIesAdmin) {
        // Cas improbable (IES_ADMIN créant un IES_ADMIN) – on désactive et pré-remplit
        sigleIesCtrl?.disable();
        sigleIesCtrl?.setValue(this.currentUserIesSigle);
        sigleEtabCtrl?.disable();
        sigleIesCtrl?.setValidators([Validators.required]);
        sigleEtabCtrl?.clearValidators();
      } else {
        // SUPER_ADMIN créant un IES_ADMIN
        this.loadIesList();
        sigleIesCtrl?.enable();
        sigleEtabCtrl?.disable();
        sigleIesCtrl?.setValidators([Validators.required]);
        sigleEtabCtrl?.clearValidators();
      }
    } else if (type === UserType.SCA_ETABLISSEMENT_ADMIN) {
      if (this.isCurrentUserIesAdmin && this.currentUserIesSigle) {

        sigleIesCtrl?.disable();
        sigleIesCtrl?.setValue(this.currentUserIesSigle);
        sigleEtabCtrl?.enable();
        // Charger les établissements avec le sigle
        this.onIesSelectionChange(this.currentUserIesSigle);
      } else {
        // SUPER_ADMIN : chargement normal
        this.loadIesList();
        sigleIesCtrl?.enable();
        sigleEtabCtrl?.disable();
      }

      // Ajout du validateur de tableau non vide
      sigleEtabCtrl?.setValidators([Validators.required, UserCreateComponent.atLeastOneSelected]);
    } else if (type === UserType.SCA_ADMIN_AUTRE_ENTITE) {
      sigleIesCtrl?.clearValidators();
      sigleEtabCtrl?.clearValidators();
      sigleIesCtrl?.disable();
      sigleEtabCtrl?.disable();
    }

    sigleIesCtrl?.updateValueAndValidity();
    sigleEtabCtrl?.updateValueAndValidity();

    // Recalcul des applications/rôles visibles selon le nouveau type cible
    this.applyVisibilityRules();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Chargement des IES
  // ─────────────────────────────────────────────────────────────────────────────
  private loadIesList(): void {
    // Ne pas charger la liste si l'admin connecté est IES_ADMIN
    if (this.isCurrentUserIesAdmin) {
      this.iesList = [];
      this.filteredIesList = [];
      return;
    }

    if (this.iesList.length > 0) return;

    this.isLoadingIes = true;
    this.groupService.getRootGroups()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (labels: string[]) => {
          this.iesList = labels.map(label => ({
            sigle: this.extractSigle(label),
            label
          }));
          this.filteredIesList = [...this.iesList];
          this.isLoadingIes = false;
        },
        error: () => {
          this.showErrorSnackbar('Impossible de charger la liste des IES');
          this.isLoadingIes = false;
        }
      });
  }

  onIesSelectionChange(sigle: string): void {
    const sigleEtabCtrl = this.userForm.get(this.SIGLE_ETABLISSEMENT);
    sigleEtabCtrl?.setValue([]);
    this.etablissementsList = [];
    this.filteredEtabList = [];
    this.etabSearchControl.setValue('');

    if (!sigle) {
      sigleEtabCtrl?.disable();
      return;
    }

    this.isLoadingEtab = true;
    this.groupService.getSubgroupsByParentSigle(sigle)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (labels: string[]) => {
          this.etablissementsList = labels.map(label => ({
            sigle: this.extractSigle(label),
            label
          }));
          this.filteredEtabList = [...this.etablissementsList];
          this.isLoadingEtab = false;
          if (this.selectedUserType === UserType.SCA_ETABLISSEMENT_ADMIN) {
            sigleEtabCtrl?.enable();
          }
        },
        error: () => {
          this.showErrorSnackbar('Impossible de charger les établissements');
          this.isLoadingEtab = false;
        }
      });
  }

  private extractSigle(label: string): string {
    const match = label.match(/\(([^)]+)\)\s*$/);
    return match ? match[1].trim() : label.trim();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Navigation entre onglets
  // ─────────────────────────────────────────────────────────────────────────────
  goToNextTab(): void {
    if (this.activeTab === 1) {

      if (this.userForm.invalid) {
        this.markFormGroupTouched();
        this.showErrorSnackbar('Veuillez remplir correctement tous les champs obligatoires');
        return;
      }

      const type = this.userForm.get(this.USER_TYPE)?.value;

      // Pour IES_ADMIN connecté, le sigle IES est déjà pré-rempli et désactivé → pas besoin de le vérifier
      if (!this.isCurrentUserIesAdmin) {
        if (type === UserType.SCA_IES_ADMIN && !this.userForm.get(this.SIGLE_IES)?.value) {
          this.userForm.get(this.SIGLE_IES)?.markAsTouched();
          this.showErrorSnackbar('Le sigle IES est obligatoire pour un administrateur IES');
          return;
        }
      }

      if (type === UserType.SCA_ETABLISSEMENT_ADMIN) {

        if (!this.userForm.get(this.SIGLE_IES)?.value) {
          this.userForm.get(this.SIGLE_IES)?.markAsTouched();
          this.showErrorSnackbar('Veuillez sélectionner une IES');
          return;
        }
        const etabValue = this.userForm.get(this.SIGLE_ETABLISSEMENT)?.value;
        if (!etabValue || etabValue.length === 0) {
          this.userForm.get(this.SIGLE_ETABLISSEMENT)?.markAsTouched();
          this.showErrorSnackbar('Veuillez sélectionner au moins un établissement');
          return;
        }
      }

      this.activeTab = 2;
    }
    else if (this.activeTab === 2) {
      this.activeTab = 3;
    }
  }

  goToTab(tabNumber: number): void {
    if (this.canGoToTab(tabNumber)) this.activeTab = tabNumber;
  }

  private canGoToTab(tabNumber: number): boolean {
    return tabNumber === 2 ? this.userForm.valid : true;
  }

  goToPreviousTab(): void {
    if (this.activeTab > 1) this.goToTab(this.activeTab - 1);
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Soumission du formulaire
  // ─────────────────────────────────────────────────────────────────────────────
  onSubmit(): void {
    if (!this.userForm.valid || this.activeTab !== 3) {
      this.markFormGroupTouched();
      this.showErrorSnackbar('Formulaire invalide');
      return;
    }

    this.isLoading = true;
    const formValue = this.userForm.getRawValue();

    // console.log("QUOI : ", formValue.sigleEtablissement);

    const userData: CreateUserRequest = {
      username: formValue.username,
      email: formValue.email,
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      enabled: true,
      emailVerified: false,
      requiredActions: ['UPDATE_PASSWORD'],
      userType: formValue.userType,
      sigleIes: formValue.userType !== UserType.SCA_SUPER_ADMIN ? formValue.sigleIes : null,
      sigleEtablissements: formValue.userType === UserType.SCA_ETABLISSEMENT_ADMIN ? formValue.sigleEtablissement : null,
      groups: []
    };


    this.userService.createUser(userData).subscribe({
      next: (response) => {
        const userId = response.userId || this.getUserIdFromResponse(response);
        if (userId && this.selectedApplications.length) {
          this.saveUserRoles(userId);
        } else {
          this.isLoading = false;
          this.showSuccessSnackbar('Utilisateur créé avec succès');
          this.resetForm();
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.showErrorSnackbar(err.error?.error || 'Erreur lors de la création');
      }
    });
  }

  getSelectedEtablissementsLabels(): string {
    const sigles = this.userForm.get(this.SIGLE_ETABLISSEMENT)?.value || [];
    // return sigles.map((sigle: string) => {
    //   const etab = this.etablissementsList.find(e => e.sigle === sigle);
    //   return etab ? etab.label : sigle;
    // }).join(', ');
    return sigles.join(', ');
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Applications & Rôles
  // ─────────────────────────────────────────────────────────────────────────────
  // private loadApplications(): void {
  //   const types = this.rolePermissionService.hasRole('SCA_SUPER_ADMIN')
  //     ? ['external-app', 'internal-app']
  //     : ['external-app'];

  //   // console.log("TYPES YI : ", types);

  //   this.clientService.getClientsByTypesWithRoles(types).subscribe({
  //     next: (clientsWithRoles) => {
  //       // console.log("PHASE 1 : ", clientsWithRoles);

  //       this.applications = clientsWithRoles.map(c => ({
  //         id: c.id,
  //         clientId: c.clientId,
  //         name: c.clientId,
  //         roles: c.roles.map(r => ({ id: r.id, name: r.name, title: r.title })),
  //         expanded: false
  //       }));

  //       // console.log("APP YI : ", this.applications);

  //       this.initializeRoleSelection();
  //       this.updateFilteredApplications();
  //     },
  //     error: (err) => this.showErrorSnackbar('Erreur chargement applications')
  //   });
  // }

  private readonly allowedClientsAutreEntite = ['indicateurs-sigesr'];
  private readonly allowedRolesAutreEntite = ['ceps_analyst', 'viewer_dashboard'];


  private loadApplications(): void {
    const types = this.rolePermissionService.hasRole('SCA_SUPER_ADMIN')
      ? ['external-app', 'internal-app']
      : ['external-app'];

    this.clientService.getClientsByTypesWithRoles(types).subscribe({
      next: (clientsWithRoles) => {
        this.allApplications = clientsWithRoles.map(c => ({
          id: c.id,
          clientId: c.clientId,
          name: c.clientId,
          roles: c.roles.map(r => ({ id: r.id, name: r.name, title: r.title }))
        }));
        this.applyVisibilityRules();
      },
      error: () => this.showErrorSnackbar('Erreur chargement applications')
    });
  }



  /**
 * Reconstruit `roleSelection` en préservant les sélections des rôles
 * toujours visibles, et en purgeant celles devenues invisibles.
 */
  private rebuildRoleSelection(): void {
    const previous = this.roleSelection;
    const next: RoleSelection = {};

    this.applications.forEach(app => {
      next[app.id] = {};
      app.roles?.forEach(role => {
        next[app.id][role.id] = previous[app.id]?.[role.id] ?? false;
      });
    });

    this.roleSelection = next;
  }

  private initializeRoleSelection(): void {
    this.roleSelection = {};
    this.applications.forEach(app => {
      this.roleSelection[app.id] = {};
      app.roles?.forEach(role => { this.roleSelection[app.id][role.id] = false; });
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Application des règles de visibilité (recalcul de `applications`)
  // ─────────────────────────────────────────────────────────────────────────────
  private applyVisibilityRules(): void {
    const rules = UserCreateComponent.VISIBILITY_RULES[this.selectedUserType];
    const isAutreEntite = this.selectedUserType === UserType.SCA_ADMIN_AUTRE_ENTITE;

    this.applications = this.allApplications
      .filter(app => isAutreEntite
        ? this.allowedClientsAutreEntite.includes(app.clientId)
        : !rules || !this.isApplicationExcluded(app, rules.excludedApplications))
      .map(app => ({
        ...app,
        roles: (app.roles || []).filter(role => isAutreEntite
          ? this.allowedRolesAutreEntite.includes(role.name)
          : !rules || !this.isRoleExcluded(role, rules.excludedRolePatterns))
      }))
      .filter(app => (app.roles?.length ?? 0) > 0);

    if (this.expandedAppId && !this.applications.some(a => a.id === this.expandedAppId)) {
      this.expandedAppId = null;
    }

    this.rebuildRoleSelection();
    this.updateSelectedApplications();
    this.updateFilteredApplications();
  }

  private isApplicationExcluded(app: Application, excludedNames: string[]): boolean {
    if (!excludedNames.length) return false;
    const haystack = `${app.id} ${app.name} ${app.clientId}`.toLowerCase();
    return excludedNames.some(name => haystack.includes(name.toLowerCase()));
  }

  private isRoleExcluded(role: Role, patterns: RegExp[]): boolean {
    if (!patterns.length) return false;
    const haystack = `${role.name || ''} ${role.title || ''}`;
    return patterns.some(pattern => pattern.test(haystack));
  }

  updateFilteredApplications(): void {
    if (!this.appSearchTerm) {
      this.filteredApplications = [...this.applications];
    } else {
      const term = this.appSearchTerm.toLowerCase();
      this.filteredApplications = this.applications.filter(a => a.name.toLowerCase().includes(term));
    }
  }

  isRoleSelected(app: Application, role: Role): boolean {
    return this.roleSelection[app.id]?.[role.id] || false;
  }

  isAppFullySelected(app: Application): boolean {
    if (!app.roles?.length) return false;
    return app.roles.every(role => this.roleSelection[app.id]?.[role.id]);
  }

  isAppPartiallySelected(app: Application): boolean {
    if (!app.roles?.length) return false;
    const selected = app.roles.filter(role => this.roleSelection[app.id]?.[role.id]).length;
    return selected > 0 && selected < app.roles.length;
  }

  toggleAppSelection(app: Application, event: any): void {
    const isSelected = event.target.checked;
    if (!this.roleSelection[app.id]) this.roleSelection[app.id] = {};
    app.roles?.forEach(role => { this.roleSelection[app.id][role.id] = isSelected; });
    this.updateSelectedApplications();
  }

  toggleRoleSelection(app: Application, role: Role, event: any): void {
    if (!this.roleSelection[app.id]) this.roleSelection[app.id] = {};
    this.roleSelection[app.id][role.id] = event.target.checked;
    this.updateSelectedApplications();
  }

  private updateSelectedApplications(): void {
    this.selectedApplications = this.applications
      .map(app => ({ ...app, roles: app.roles?.filter(role => this.isRoleSelected(app, role)) || [] }))
      .filter(app => app.roles && app.roles.length > 0);
  }

  getSelectedRolesCount(app: Application): number {
    if (!app.roles) return 0;
    return app.roles.filter(role => this.roleSelection[app.id]?.[role.id]).length;
  }

  getTotalSelectedRoles(): number {
    let total = 0;
    Object.values(this.roleSelection).forEach(appRoles => {
      Object.values(appRoles).forEach(selected => { if (selected) total++; });
    });
    return total;
  }

  resetAllRoles(): void {
    Object.keys(this.roleSelection).forEach(appId => {
      Object.keys(this.roleSelection[appId]).forEach(roleId => {
        this.roleSelection[appId][roleId] = false;
      });
    });
    this.updateSelectedApplications();
    this.showSuccessSnackbar('Rôles réinitialisés');
  }

  toggleAccordion(appId: string): void {
    this.expandedAppId = this.expandedAppId === appId ? null : appId;
  }

  isAppExpanded(appId: string): boolean {
    return this.expandedAppId === appId;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Sauvegarde des rôles
  // ─────────────────────────────────────────────────────────────────────────────
  private saveUserRoles(userId: string): void {
    this.isSaving = true;
    const requests = this.selectedApplications.flatMap(app =>
      app.roles!.map(role => this.roleService.assignClientRole(userId, role.id, app.id))
    );

    forkJoin(requests).subscribe({
      next: () => {
        this.isSaving = false;
        this.isLoading = false;
        this.notificationService.showSuccess({
          title: 'Succès',
          message: `Utilisateur créé avec ${requests.length} rôle(s)`,
          duration: 4000
        });
        this.resetForm();
      },
      error: () => {
        this.isSaving = false;
        this.isLoading = false;
        this.showErrorSnackbar('Erreur lors de l\'assignation des rôles');
      }
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Utilitaires
  // ─────────────────────────────────────────────────────────────────────────────
  private getUserIdFromResponse(response: any): string {
    return response?.location?.split('/').pop() || '';
  }

  onCancel(): void {
    if (this.selectedApplications.length && !confirm('Quitter sans sauvegarder ?')) return;
    this.resetForm();
  }

  private resetForm(): void {
    this.userForm.reset();
    this.activeTab = 1;
    this.selectedApplications = [];
    this.roleSelection = {};
    this.expandedAppId = null;
    this.etablissementsList = [];
    this.iesList = [];
    this.filteredIesList = [];
    this.filteredEtabList = [];
    this.isIesDropdownOpen = false;
    this.isEtabDropdownOpen = false;
    this.iesSearchControl.setValue('');
    this.etabSearchControl.setValue('');
    this.appSearchTerm = '';
    const firstAllowed = this.userTypes.find(t => t.allowed);
    if (firstAllowed) {
      this.userForm.get(this.USER_TYPE)?.setValue(firstAllowed.value);
      this.onUserTypeChange(firstAllowed.value);
    }
  }

  private markFormGroupTouched(): void {
    Object.values(this.userForm.controls).forEach(c => c.markAsTouched());
  }

  private showErrorMessage(m: string): void {
    this.snackBar.open(m, 'Fermer', { duration: 5000 });
  }

  showSuccessSnackbar(m: string): void {
    this.snackbarMessage = m;
    this.isErrorSnackbar = false;
    this.showSnackbar = true;
    setTimeout(() => this.hideSnackbar(), 5000);
  }

  showErrorSnackbar(m: string): void {
    this.snackbarMessage = m;
    this.isErrorSnackbar = true;
    this.showSnackbar = true;
    setTimeout(() => this.hideSnackbar(), 5000);
  }

  hideSnackbar(): void {
    this.showSnackbar = false;
  }

  // Méthodes pour le multi-select
  toggleEtabDropdown(): void {
    // this.isEtabDropdownOpen = !this.isEtabDropdownOpen;
    this.isEtabDropdownOpen = !this.isEtabDropdownOpen;

    if (this.isEtabDropdownOpen) {
      this.etabSearchControl.setValue('');
      this.filteredEtabList = [...this.etablissementsList];
      this.isIesDropdownOpen = false;
    }
  }

  isEtabSelected(sigle: string): boolean {
    const current = this.userForm.get(this.SIGLE_ETABLISSEMENT)?.value || [];
    return current.includes(sigle);
  }

  toggleEtabSelection(sigle: string, event: any): void {
    const control = this.userForm.get(this.SIGLE_ETABLISSEMENT);
    let current = control?.value || [];
    if (event.target.checked) {
      current = [...current, sigle];
    } else {
      current = current.filter((s: string) => s !== sigle);
    }
    control?.setValue(current);
    control?.markAsTouched();
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  Multi-select Établissements : "Sélectionner tout"
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Vrai si TOUS les établissements actuellement visibles (donc filtrés par
   * la recherche) sont déjà sélectionnés.
   */
  isAllEtabSelected(): boolean {
    if (this.filteredEtabList.length === 0) return false;
    const current: string[] = this.userForm.get(this.SIGLE_ETABLISSEMENT)?.value || [];
    return this.filteredEtabList.every(e => current.includes(e.sigle));
  }

  /**
   * Vrai si AU MOINS UN établissement visible est sélectionné,
   * mais pas tous → sert à afficher l'état "indeterminate" de la checkbox.
   */
  isSomeEtabSelected(): boolean {
    if (this.filteredEtabList.length === 0) return false;
    const current: string[] = this.userForm.get(this.SIGLE_ETABLISSEMENT)?.value || [];
    const selectedCount = this.filteredEtabList.filter(e => current.includes(e.sigle)).length;
    return selectedCount > 0 && selectedCount < this.filteredEtabList.length;
  }

  /**
   * Coche ou décoche TOUS les établissements actuellement visibles.
   * Si l'utilisateur a filtré la liste (recherche), seuls les éléments
   * filtrés sont impactés.
   */
  toggleAllEtab(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const ctrl = this.userForm.get(this.SIGLE_ETABLISSEMENT);
    if (!ctrl) return;

    const current: string[] = ctrl.value || [];
    const visibleSigles = this.filteredEtabList.map(e => e.sigle);

    let next: string[];
    if (checked) {
      // Union : on garde les sélections hors du filtre courant
      const set = new Set([...current, ...visibleSigles]);
      next = Array.from(set);
    } else {
      // On retire uniquement les éléments visibles
      const set = new Set(current);
      visibleSigles.forEach(s => set.delete(s));
      next = Array.from(set);
    }

    ctrl.setValue(next);
    ctrl.markAsTouched();
  }

  // Fermer la dropdown si on clique en dehors (optionnel, à ajouter dans ngOnInit)
  // Gérer le clic en dehors
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    // if (!target.closest('.custom-multiselect')) {
    //   this.isEtabDropdownOpen = false;
    // }

    if (!target.closest('#ies-multiselect')) {
      this.isIesDropdownOpen = false;
    }

    if (!target.closest('#etab-multiselect')) {
      this.isEtabDropdownOpen = false;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  Dropdown IES (single-select searchable)
  // ─────────────────────────────────────────────────────────────────────────

  toggleIesDropdown(): void {
    if (this.isLoadingIes) return;
    this.isIesDropdownOpen = !this.isIesDropdownOpen;

    if (this.isIesDropdownOpen) {
      // Reset du filtre et fermeture de l'autre dropdown
      this.iesSearchControl.setValue('');
      this.filteredIesList = [...this.iesList];
      this.isEtabDropdownOpen = false;
    }
  }

  isIesSelected(sigle: string): boolean {
    return this.userForm.get(this.SIGLE_IES)?.value === sigle;
  }

  selectIes(sigle: string): void {
    const ctrl = this.userForm.get(this.SIGLE_IES);
    if (!ctrl) return;

    ctrl.setValue(sigle);
    ctrl.markAsTouched();

    // Déclenche le chargement des établissements + reset
    this.onIesSelectionChange(sigle);

    this.isIesDropdownOpen = false;
    this.iesSearchControl.setValue('');
  }

  getSelectedIesLabel(): string {
    const sigle = this.userForm.get(this.SIGLE_IES)?.value;
    if (!sigle) return '';
    const ies = this.iesList.find(i => i.sigle === sigle);
    return ies ? ies.label : sigle;
  }
}
